"use client";

import { Shield } from "lucide-react";
import { useTranslations } from "next-intl";

import { useRecipeFrom } from "@/app/hooks/useRecipeOrigin";
import { useSession } from "@/app/hooks/useSession";
import { NomboxLogo } from "@/components/shared/NomboxLogo";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { NAV_DESTINATIONS } from "./destinations";
import { NavFooter } from "./NavFooter";

// 220px fixed-left sidebar. Hidden below md. Mounted in [locale]/layout.tsx.
export function DesktopRail() {
  const pathname = usePathname();
  const from = useRecipeFrom();
  const t = useTranslations("nav");
  const { isAuthenticated, data: sessionData } = useSession();
  const isAdmin = (sessionData?.user as any)?.role === "admin";

  return (
    <aside
      aria-label={t("primary")}
      className="sticky top-0 hidden md:flex md:h-screen md:w-[220px] md:shrink-0 md:flex-col md:border-r md:border-border md:bg-surface-glass md:backdrop-blur-lg md:backdrop-saturate-150"
    >
      <Link
        href={isAuthenticated ? "/discover" : "/welcome"}
        className="flex items-center gap-2 px-3 py-5"
      >
        <NomboxLogo className="text-foreground" />
        <span className="text-lg font-semibold">Nombox</span>
      </Link>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
        {NAV_DESTINATIONS.map((d) => {
          const Icon = d.icon;
          const active = isActive(pathname, d.href, from);
          return (
            <Link
              key={d.id}
              href={d.href}
              prefetch={true}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-base transition-colors",
                active
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="size-5" aria-hidden />
              <span>{t(d.i18nKey)}</span>
            </Link>
          );
        })}
        {isAdmin && (
          <Link
            href="/admin"
            prefetch={true}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-base transition-colors",
              isActive(pathname, "/admin", from)
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            )}
            aria-current={isActive(pathname, "/admin", from) ? "page" : undefined}
          >
            <Shield className="size-5" aria-hidden />
            <span>{t("admin")}</span>
          </Link>
        )}
      </nav>
      <NavFooter variant="rail" />
    </aside>
  );
}

function isActive(pathname: string, href: string, from: string | null): boolean {
  if (href === "/") return pathname === "/";

  const isRecipeSub = /^\/recipes\//.test(pathname);
  if (isRecipeSub && from) {
    return href === `/${from}`;
  }

  return pathname === href || pathname.startsWith(href + "/");
}
